// Client-persisted key registry — every localStorage/sessionStorage key the
// console writes is declared here. Raw string literals to storage APIs are
// forbidden outside this module (a scattered literal is an untracked schema
// change waiting to ship).
//
// Retention classes — teardownSession (src/lib/session.ts) enforces the split:
//   tenant — dies at logout / session loss / account switch:
//            SK.activeOrg, `${NS}api-key:*`, `${NS}mpu:*`
//   prefs  — survives logout across account switches: everything else,
//            INCLUDING `${NS}ref-bound:*` (the backend owns binding
//            idempotency; the latch only silences repeat UX on this
//            browser).
//
// These keys are a persisted contract: renaming or reshaping one after launch
// requires a migration forever. STORE_VERSION + migrateLegacyStorage() is the
// seam — bump the version and add a branch when a name or value shape changes.

const NS = 'togence:'

/** Fixed-name keys. Values are opaque strings unless noted. */
export const SK = {
  // tenant-bound
  activeOrg: `${NS}active-org`,
  // prefs
  locale: `${NS}locale`,
  theme: `${NS}theme`,
  /** JSON Record<groupKey, boolean> — sidebar group collapse state. */
  navCollapsed: `${NS}nav-collapsed`,
  deployExpert: `${NS}deploy-expert`,
  trainingAdvanced: `${NS}training-advanced`,
  playgroundAdvanced: `${NS}playground-advanced`,
  /** sessionStorage — id of the announcement dismissed on landing this tab
   *  session (was 'true' pre-board: a stale 'true' simply never matches a
   *  real id, so the old shape fails open to "show" — no migration needed
   *  for a session-scoped key). */
  landingAnnouncementClosed: `${NS}landing-announcement`,
  /** sessionStorage — scroll offset on `/` when leaving for `/calculator`, consumed
   *  once by Landing on remount (the browser's own traversal restore lands on the
   *  lazy route's skeleton and gets clamped there). Self-clearing, session-scoped. */
  landingScroll: `${NS}landing-scroll`,
  /** localStorage — id of the pinned announcement the user dismissed from
   *  the console banner; a NEW pinned announcement reappears (its id never
   *  matches the stored one). Prefs class — survives account switch. */
  announcementBannerDismissed: `${NS}announcement-banner`,
  acquisitionEntry: `${NS}acq-entry`,
  acquisitionSource: `${NS}acq-source`,
  /** localStorage — the pending referral code (inviter org id) captured from
   *  a `?ref=` invite link, consumed by the referral bind on the org's first
   *  authenticated interaction. */
  acquisitionRef: `${NS}acq-ref`,
  storeVersion: `${NS}store-version`,
} as const

/** Tenant-bound key prefixes — teardown wipes every key under these in BOTH
 *  stores (a stray localStorage copy of a session key must not survive). */
export const TENANT_KEY_PREFIXES = [`${NS}api-key:`, `${NS}mpu:`] as const

/** sessionStorage — per-org gateway sk-* key (explicit Save/create only). */
export const apiKeyKey = (orgId: string) => `${NS}api-key:${orgId}` as const
/** localStorage — multipart upload resume entry {uploadId,key,partSize,etagByPart}. */
export const mpuKey = (sha256: string) => `${NS}mpu:${sha256}` as const
/** localStorage — referral-bind latch per org (prefs class: survives). */
export const refBoundKey = (orgId: string) => `${NS}ref-bound:${orgId}` as const

const STORE_VERSION = '2'

// ── v0 → v1: namespace adoption ──────────────────────────────────────────
// Carry the three durable prefs forward; drop the rest of the unprefixed
// generation — session-bound leftovers (sk-*, mpu resume) are dead state.

const V0_CARRY: ReadonlyArray<readonly [string, string]> = [
  ['activeOrgId', SK.activeOrg],
  ['theme', SK.theme],
  ['locale', SK.locale],
]
const V0_DROP = [
  'nav:collapsed',
  'deployments_expert',
  'training_advanced',
  'playground_advanced_open',
  'acquisition:entry',
  'acquisition:entry:source',
  'landing_announcement_closed',
]
const V0_DROP_PREFIX = ['inference_api_key:', 'upload:mpu:', 'acquisition:grant:']

// ── v1 → v2: the launch-grant claim latches are dead keys ────────────────
// ADR-0042 retired the unconditional launch grant; `togence:grant:<orgId>`
// markers only ever silenced repeat claim UX, and the referral bind carries
// its own latch (`togence:ref-bound:<orgId>`), so the old entries are dropped
// outright — nothing reads them and the backend owns the real idempotency.
const V1_DROP_PREFIX = [`${NS}grant:`]

export function migrateLegacyStorage(): void {
  try {
    if (typeof window === 'undefined' || localStorage.getItem(SK.storeVersion) === STORE_VERSION) return
    const fromV0 = localStorage.getItem(SK.storeVersion) === null
    if (fromV0) {
      for (const [from, to] of V0_CARRY) {
        const v = localStorage.getItem(from)
        if (v !== null && localStorage.getItem(to) === null) localStorage.setItem(to, v)
        localStorage.removeItem(from)
      }
      for (const store of [localStorage, sessionStorage]) {
        for (const k of V0_DROP) store.removeItem(k)
        for (const k of Object.keys(store)) {
          if (V0_DROP_PREFIX.some((p) => k.startsWith(p))) store.removeItem(k)
        }
      }
    }
    for (const store of [localStorage, sessionStorage]) {
      for (const k of Object.keys(store)) {
        if (V1_DROP_PREFIX.some((p) => k.startsWith(p))) store.removeItem(k)
      }
    }
    localStorage.setItem(SK.storeVersion, STORE_VERSION)
  } catch {
    // storage unavailable — migration is best-effort, never throws
  }
}

// Runs at module eval so the earliest consumer (theme-init fallback aside,
// i18n locale resolution runs during `import '@/i18n'`) already sees migrated
// keys — the seam must precede every reader regardless of import order.
migrateLegacyStorage()
